import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import useExcelUrl from "app/useExcelUrl";
import {
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridLinkColumn,
  GridColumns,
  PageHeader,
} from "common";
import useAccess from "features/profile/useAccess";
import I18n from "i18n";
import { useState } from "react";
import { ProgramItem } from "./programApi";
import useProgramList from "./useProgramList";
export interface ProgramListProps extends Omit<DataGridBaseBaseProps, "id"> {
  showHeader?: boolean;
}

export default function ProgramList({
  showHeader = true,
  ...props
}: ProgramListProps) {
  const { params, scope, editUrl, ...service } = useProgramList();
  const { hasPermissions, canEditItem } = useAccess();
  const [section, setSection] = useState<string | undefined>(() => {
    try {
      const saved = sessionStorage.getItem("grid.programs");
      return saved ? JSON.parse(saved)?.scope || "active" : "active";
    } catch {
      return "active";
    }
  });
  const excelUrl = useExcelUrl(
    "programs",
    scope,
    section ? { scope: section } : undefined,
    false,
  );
  let columns: GridColumns<ProgramItem> = [
    {
      field: "name",
      headerName: I18n.t("Program.attributes.name"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`${editUrl}${params.row?.id}`}
          params={params}
        />
      ),
      flex: 1,
    },
    {
      field: "display_name",
      headerName: I18n.t("Program.attributes.display_name"),
      flex: 1,
    },
    {
      field: "color",
      headerName: I18n.t("Program.attributes.color"),
      type: "color",
      flex: 1,
    },
    { field: "code", headerName: I18n.t("Program.attributes.code"), flex: 1 },
    { field: "sku", headerName: I18n.t("Program.attributes.sku"), flex: 1 },
    {
      field: "duration",
      headerName: I18n.t("Program.attributes.duration"),
      type: "number",
    },
    {
      field: "partner_booking_close_minutes",
      headerName: I18n.t("Program.attributes.partner_booking_close_minutes"),
      type: "number",
    },
    {
      field: "always_visible",
      headerName: I18n.t("Program.attributes.always_visible"),
      type: "boolean",
    },
    {
      field: "armband",
      headerName: I18n.t("Program.attributes.armband"),
      type: "boolean",
    },
    {
      field: "has_options",
      headerName: I18n.t("Program.attributes.has_options"),
      type: "boolean",
    },
    {
      field: "sort_by",
      headerName: I18n.t("Program.attributes.sort_by"),
      type: "number",
    },
    {
      field: "active",
      headerName: I18n.t("Program.attributes.active"),
      type: "boolean",
    },
    {
      field: "discontinued_at",
      headerName: I18n.t("Program.attributes.discontinued_at"),
      type: "date",
    },
    {
      field: "partner_visible",
      headerName: I18n.t("Program.attributes.partner_visible"),
      type: "boolean",
    },
    {
      field: "ready_tracking",
      headerName: I18n.t("Program.attributes.ready_tracking"),
      type: "boolean",
    },
    {
      field: "kid_ticket_available",
      headerName: I18n.t("Program.attributes.kid_ticket_available"),
      type: "boolean",
    },
  ];

  return (
    <>
      {showHeader && (
        <PageHeader
          avatar={<TheaterComedyIcon />}
          title={I18n.t("Program.title")}
        />
      )}
      <DataGrid
        sections={[
          { value: "active", label: I18n.t("Program.activePrograms") },
          { value: "inactive", label: I18n.t("Program.inactivePrograms") },
        ]}
        onSectionChange={setSection}
        {...service}
        defaultState={{ scope: "active" }}
        columns={columns}
        id="programs"
        excelUrl={excelUrl}
        paperProps={{
          sx:
            section === "inactive"
              ? { "& tbody tr": { opacity: 0.5, filter: "grayscale(1)" } }
              : undefined,
        }}
        {...props}
        //getHeaderName={field => I18n.t(`Program.attributes.${field.replaceAll(".", "_")}`)}
        canOrderRows
        editUrl={`${editUrl}:id`}
        canCreate={hasPermissions("program.create")}
        canDelete={hasPermissions(["program.editany", "program.editown"])}
        canEdit={hasPermissions(["program.editany", "program.editown"])}
        canDeleteRow={params => canEditItem("program", params.row)}
        canEditRow={params => canEditItem("program", params.row)}
        canBatchActions={hasPermissions(["program.editany", "program.editown"])}
        canHandleTrash={hasPermissions(["program.editany", "program.editown"])}
      />
    </>
  );
}
